/**
 * MiniSearch-backed client search provider.
 *
 * @module
 */
import MiniSearch from 'minisearch';
import { extractFieldText, filterFieldsOf, matchesFilters, matchesText, parseQuery, } from '@molecule/app-client-search';
/**
 * One index: MiniSearch for the text, plus the original documents kept by id
 * so hits return them untouched and filters read real values, not tokens.
 */
class MinisearchIndex {
    options;
    docs = new Map();
    mini;
    extract;
    combineWith;
    /**
     * Builds the index.
     *
     * @param docs - The documents.
     * @param options - How to index them.
     * @param config - Provider configuration.
     */
    constructor(docs, options, config) {
        this.options = options;
        this.combineWith = config.combineWith ?? 'AND';
        const extract = options.extractField ?? ((doc, field) => extractFieldText(doc, field));
        this.extract = extract;
        this.mini = new MiniSearch({
            idField: options.idField,
            fields: options.fields,
            storeFields: [],
            extractField: (doc, field) => field === options.idField ? String(doc[field]) : extract(doc, field),
        });
        for (const doc of docs)
            this.docs.set(String(doc[options.idField]), doc);
        this.mini.addAll(docs);
    }
    /** Number of documents in the index. */
    get size() {
        return this.docs.size;
    }
    /**
     * Searches the index; a string goes through the core's query grammar first.
     *
     * @param query - Text, or a structured query.
     * @returns Matching documents, best first (insertion order in browse mode).
     */
    search(query) {
        const q = typeof query === 'string'
            ? parseQuery(query, { filterFields: filterFieldsOf(this.options) })
            : query;
        const filters = q.filters ?? [];
        const phrases = q.phrases ?? [];
        const exclude = q.exclude ?? [];
        const text = q.text.trim();
        const passes = (doc) => matchesFilters(doc, filters) &&
            matchesText(doc, this.options.fields, phrases, exclude, this.extract);
        let hits;
        if (!text) {
            // Browse mode: everything the constraints accept, in insertion order.
            hits = [];
            for (const [id, doc] of this.docs) {
                if (passes(doc))
                    hits.push({ id, score: 0, doc, terms: [], fields: [] });
            }
        }
        else {
            // Only set what is defined: an explicit `undefined` would override
            // MiniSearch's own default for that option.
            const base = {
                prefix: this.options.prefix ?? true,
                filter: (r) => {
                    const doc = this.docs.get(String(r.id));
                    return !!doc && passes(doc);
                },
            };
            if (this.options.fuzzy !== false)
                base.fuzzy = this.options.fuzzy ?? 0.2;
            if (this.options.boost)
                base.boost = this.options.boost;
            // MiniSearch reports only the query terms that matched, so count the typed ones here.
            const termCount = text.split(/[^\p{L}\p{N}]+/u).filter(Boolean).length;
            let results = this.mini.search(text, { ...base, combineWith: this.combineWith });
            // A query MiniSearch tokenizes into several terms (spaces, or an unknown
            // `word:thing` kept as text) may match none of them together: rank the
            // union, but only documents matching at least half of the terms, so a
            // query with one real word among nonsense does not return loose matches.
            if (!results.length && this.combineWith === 'AND') {
                results = this.mini
                    .search(text, { ...base, combineWith: 'OR' })
                    .filter((r) => r.queryTerms.length * 2 >= termCount);
            }
            hits = results.map((r) => ({
                id: String(r.id),
                score: r.score,
                doc: this.docs.get(String(r.id)),
                terms: r.terms,
                fields: Array.from(new Set(Object.values(r.match).flat())),
            }));
            // Several words typed together are usually a phrase ("user feedback"):
            // a document that contains them adjacent, in any searched field, ranks
            // above one that merely contains each of them somewhere.
            const needle = text.toLowerCase().replace(/\s+/g, ' ');
            if (needle.includes(' ')) {
                for (const h of hits) {
                    if (this.options.fields.some((f) => this.extract(h.doc, f).toLowerCase().includes(needle))) {
                        h.score *= 2.5;
                    }
                }
                hits.sort((a, b) => b.score - a.score);
            }
        }
        return q.limit != null ? hits.slice(0, q.limit) : hits;
    }
    /**
     * Completions for a partial term.
     *
     * @param prefix - What has been typed so far.
     * @param limit - Maximum suggestions. Defaults to `5`.
     * @returns Suggested terms, most likely first.
     */
    suggest(prefix, limit = 5) {
        if (!prefix.trim())
            return [];
        return this.mini
            .autoSuggest(prefix, {
            prefix: true,
            ...(this.options.fuzzy === false ? {} : { fuzzy: this.options.fuzzy ?? 0.2 }),
        })
            .slice(0, limit)
            .map((s) => s.suggestion);
    }
    /**
     * Adds a document.
     *
     * @param doc - The document; its id must not already be indexed.
     */
    add(doc) {
        const id = String(doc[this.options.idField]);
        if (this.docs.has(id))
            throw new Error(`@molecule/app-client-search-minisearch: id "${id}" is already indexed; use replace()`);
        this.docs.set(id, doc);
        this.mini.add(doc);
    }
    /**
     * Replaces the document with the same id, or adds it.
     *
     * @param doc - The document.
     */
    replace(doc) {
        const id = String(doc[this.options.idField]);
        if (this.docs.has(id))
            this.mini.discard(id);
        this.docs.set(id, doc);
        this.mini.add(doc);
    }
    /**
     * Removes a document by id; unknown ids are ignored.
     *
     * @param id - The document id.
     */
    remove(id) {
        if (!this.docs.has(id))
            return;
        this.docs.delete(id);
        this.mini.discard(id);
    }
}
/**
 * Creates a MiniSearch client search provider.
 *
 * @param config - Provider configuration.
 * @returns A client search provider.
 */
export function createProvider(config = {}) {
    return {
        name: 'minisearch',
        createIndex(docs, options) {
            return new MinisearchIndex(docs, options, config);
        },
    };
}
/** Default MiniSearch provider instance. */
export const provider = createProvider();
